import { Page } from "../../components/layout/Page";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import {
  Box,
  Button,
  CloseButton,
  LoadingOverlay,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import {
  ArrowRightIcon,
  CheckIcon,
  InfoIcon,
  PencilSimpleIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useSwr from "swr";
import { InvoiceApi } from "../../apis";
import type { IProposeAmendmentBody } from "../../apis/invoice";
import { getAmendErrorMessage, handleAmendError } from "../../components/invoice-amendment/amend-error";
import {
  countAmendLineChanges,
  deriveLineMarker,
  InvoiceProductForm,
  type InvoiceProductFormHandle,
} from "../../components/sales-invoice/invoice-product-form";
import productClasses from "../../components/sales-invoice/invoice-product-form.module.css";
import { usePermission } from "../../components/permission";
import { AppModalHeader, ModalFooter } from "../../components/ui/app-modal";
import modalChrome from "../../components/ui/app-modal.module.css";
import { useInvoiceFormData } from "../../hooks/useInvoiceFormData";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { dateFormat, dateTimeFormat } from "../../utils/date";
import { lineTotal } from "../../utils/invoice-totals";
import { message } from "../../utils/Message";
import { renderBusinessName } from "../../utils/render-business-name";
import classes from "./amend.module.css";
import { dueDay } from "../../utils/due-day";

type FormValues = {
  reason: string;
  receiverBusinessId: string;
  lines: any[];
  additionalLines: any[];
  discountType?: string | null;
  discountValue?: number | null;
  shippingAmount?: number | null;
  description?: string | null;
};

const DRY_RUN_REASON_PLACEHOLDER = "-";

const positiveOrNull = (v: unknown) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

function buildProposeBody(values: FormValues, dryRun: boolean): IProposeAmendmentBody {
  const trimmedReason = values.reason.trim();
  return {
    reason: trimmedReason || (dryRun ? DRY_RUN_REASON_PLACEHOLDER : ""),
    lines: (values.lines ?? [])
      .filter((item: any) => !item?._removed && !item?.additionalLines)
      .map((item: any) => ({
        parentVariantId: item?.parentVariantId || item?.variantParentId || item?.variantId || item?.id,
        variantId: item?.variantId || item?.id,
        warehouseId: item?.warehouseId || item?.inventories?.[0]?.warehouseId || "",
        discountType: item?.discountType || null,
        discountValue: item?.discountType ? (item?.discountValue ?? null) : null,
        quantity: Number(item?.quantity || 0),
        price: Number(item?.price || 0),
        name: item?.name || item?.nameMon || null,
        skuCode: item?.skuCode || null,
      })),
    additionalLines: (values.additionalLines ?? [])
      .filter((item: any) => !item?._removed)
      .map((item: any) => ({
        image: item?.image || null,
        name: item?.name,
        unit: item?.unit,
        price: Number(item?.price || 0),
        discountType: item?.discountType || null,
        discountValue: item?.discountType ? (item?.discountValue ?? null) : null,
        quantity: Number(item?.quantity || 0),
      })),
    discountType: values.discountType || null,
    discountValue: values.discountType ? positiveOrNull(values.discountValue) : null,
    shippingAmount: positiveOrNull(values.shippingAmount),
    description: values.description || null,
    dryRun,
  };
}

/** Money-affecting payload only — dry-run / calc match. Reason + description excluded. */
function moneyContentKey(values: FormValues) {
  const { reason: _reason, dryRun: _dry, description: _description, ...rest } = buildProposeBody(values, false);
  return JSON.stringify(rest);
}

/** Dirty snapshot includes description (not reason — reason alone is not a content edit). */
function dirtyContentKey(values: FormValues) {
  const { reason: _reason, dryRun: _dry, ...rest } = buildProposeBody(values, false);
  return JSON.stringify(rest);
}

function formatSignedAmount(n: number) {
  const abs = currencyFormat(Math.abs(n), { withSymbol: false });
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return currencyFormat(0, { withSymbol: false });
}

type ConfirmChange = {
  key: string;
  marker: "new" | "changed" | "removed";
  name: string;
  detail: string;
};

function buildConfirmChanges(
  values: FormValues,
  originalLines: any[],
  originalAdditionalLines: any[],
  t: (key: string, opts?: any) => string,
): ConfirmChange[] {
  const out: ConfirmChange[] = [];

  (values.lines ?? []).forEach((line: any, idx: number) => {
    const marker = deriveLineMarker(line, originalLines, "product");
    if (!marker) return;
    const name = line?.nameMon || line?.name || "-";
    const original = originalLines.find(
      (o) => String(o?.variantId ?? o?.id ?? "") === String(line?.variantId ?? line?.id ?? ""),
    );
    let detail = "";
    if (marker === "changed" && original) {
      const parts: string[] = [];
      if (Number(original.quantity ?? 0) !== Number(line.quantity ?? 0)) {
        parts.push(t("amend.confirmChangeQty", { from: original.quantity, to: line.quantity }));
      }
      if (Number(original.price ?? 0) !== Number(line.price ?? 0)) {
        parts.push(
          t("amend.confirmChangePrice", {
            from: currencyFormat(original.price ?? 0, { withSymbol: false }),
            to: currencyFormat(line.price ?? 0, { withSymbol: false }),
          }),
        );
      }
      if ((original.warehouseId ?? "") !== (line.warehouseId ?? "")) {
        parts.push(t("amend.changedField.warehouse"));
      }
      detail = parts.join(" · ");
    } else if (marker === "new") {
      detail = t("amend.confirmChangeNew", {
        qty: line.quantity ?? 0,
        price: currencyFormat(line.price ?? 0, { withSymbol: false }),
      });
    } else if (marker === "removed") {
      detail = t("amend.confirmChangeRemoved");
    }
    out.push({ key: `p-${idx}`, marker, name, detail });
  });

  (values.additionalLines ?? []).forEach((line: any, idx: number) => {
    const marker = deriveLineMarker(line, originalAdditionalLines, "additional");
    if (!marker) return;
    const name = line?.name || line?.nameMon || t("amend.additionalLine");
    let detail = "";
    if (marker === "new") {
      detail = t("amend.confirmChangeNew", {
        qty: line.quantity ?? 0,
        price: currencyFormat(line.price ?? 0, { withSymbol: false }),
      });
    } else if (marker === "changed") {
      detail = t("amend.confirmChangeQty", {
        from: "-",
        to: `${line.quantity ?? 0} × ${currencyFormat(lineTotal(line), { withSymbol: false })}`,
      });
    } else {
      detail = t("amend.confirmChangeRemoved");
    }
    out.push({ key: `a-${idx}`, marker, name, detail });
  });

  return out;
}

function markerPillColor(marker: "new" | "changed" | "removed") {
  if (marker === "new") return { bg: "var(--mantine-color-green-1)", color: "var(--mantine-color-green-9)" };
  if (marker === "removed") return { bg: "var(--mantine-color-red-1)", color: "var(--mantine-color-red-9)" };
  return { bg: "var(--mantine-color-yellow-1)", color: "var(--mantine-color-yellow-9)" };
}

export function SalesInvoiceAmend() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["invoice", "common"]);
  const INV_AMEND = usePermission("INV_AMEND");
  const productFormRef = useRef<InvoiceProductFormHandle>(null);

  const invoiceId = params.id ?? "";
  const { invoice, isLoading: invoiceLoading, INV_LIST } = useInvoiceFormData({ id: invoiceId });

  const { data: amendability, isLoading: amendLoading } = useSwr(
    invoiceId && INV_LIST.hasAccess ? `/api/invoice/${invoiceId}/amendability` : null,
    () => InvoiceApi.amendability(invoiceId),
  );

  const originalLines = useMemo(() => (invoice?.lines ?? []).map((line: any) => ({ ...line })), [invoice]);
  const originalAdditionalLines = useMemo(
    () => (invoice?.additionalLines ?? []).map((line: any) => ({ ...line })),
    [invoice],
  );

  const initialValues = useMemo<FormValues>(
    () => ({
      reason: "",
      receiverBusinessId: invoice?.receiverBusinessId ?? invoice?.receiver?.businessId ?? "",
      lines: (invoice?.lines ?? []).map((line: any) => ({
        ...line,
        warehouseId: line.warehouseId ?? "",
      })),
      additionalLines: invoice?.additionalLines ?? [],
      discountType: invoice?.discountType ?? null,
      discountValue: invoice?.discountValue ?? null,
      shippingAmount: invoice?.shippingAmount ?? null,
      description: invoice?.description ?? null,
    }),
    [invoice],
  );

  const initialSnapshotRef = useRef("");
  useEffect(() => {
    if (invoice) {
      initialSnapshotRef.current = dirtyContentKey({
        reason: "",
        receiverBusinessId: invoice?.receiverBusinessId ?? "",
        lines: (invoice.lines ?? []).map((line: any) => ({ ...line, warehouseId: line.warehouseId ?? "" })),
        additionalLines: invoice.additionalLines ?? [],
        discountType: invoice.discountType ?? null,
        discountValue: invoice.discountValue ?? null,
        shippingAmount: invoice.shippingAmount ?? null,
        description: invoice.description ?? null,
      });
    }
  }, [invoice]);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues,
  });

  useEffect(() => {
    if (!invoice) return;
    form.setValues({
      reason: form.values.reason,
      receiverBusinessId: invoice.receiverBusinessId ?? invoice?.receiver?.businessId ?? "",
      lines: (invoice.lines ?? []).map((line: any) => ({
        ...line,
        warehouseId: line.warehouseId ?? "",
      })),
      additionalLines: invoice.additionalLines ?? [],
      discountType: invoice.discountType ?? null,
      discountValue: invoice.discountValue ?? null,
      shippingAmount: invoice.shippingAmount ?? null,
      description: invoice.description ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const [calculatedResult, setCalculated] = useState<{
    key: string;
    previousTotal: number;
    newTotal: number;
    newVat?: number | null;
  } | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);

  const formDisabled = !amendability?.amendable || !INV_AMEND.hasAccess;
  const detailsPath =
    invoice?.invoiceStatus === "CLOSED" ? `/closed-invoice/${invoiceId}/details` : `/sales-invoice/${invoiceId}/details`;

  // Money key ignores reason/description so typing textareas does not clear calc or re-hit dry-run.
  const moneyKey = useMemo(
    () => moneyContentKey(form.values),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional field slice
    [
      form.values.lines,
      form.values.additionalLines,
      form.values.discountType,
      form.values.discountValue,
      form.values.shippingAmount,
      form.values.receiverBusinessId,
    ],
  );
  const dirtyKey = useMemo(
    () => dirtyContentKey(form.values),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional field slice
    [
      form.values.lines,
      form.values.additionalLines,
      form.values.discountType,
      form.values.discountValue,
      form.values.shippingAmount,
      form.values.receiverBusinessId,
      form.values.description,
    ],
  );
  const [debouncedMoneyKey] = useDebouncedValue(moneyKey, 500);
  const calculated = calculatedResult?.key === moneyKey ? calculatedResult : null;

  const paid = Number(invoice?.paidAmount ?? 0);
  const previousVat = Number(invoice?.vatAmount ?? 0);
  const invoiceStatusRef = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus);
  const statusRef = invoiceStatusRef?.find((x: IReference) => x.code === invoice?.invoiceStatus);
  const receiver = invoice?.receiverBusiness ?? invoice?.receiver ?? null;
  const partnerName = renderBusinessName(receiver);
  const partnerReg = receiver?.regNumber ?? receiver?.partner?.regNumber ?? null;
  const confirmedDate = invoice?.confirmedDate ? dateFormat(invoice.confirmedDate) : null;
  const dueDate = dueDay(invoice?.paymentDate);

  const isDirty = dirtyKey !== initialSnapshotRef.current;

  const lineCount =
    (form.values.lines ?? []).filter((l: any) => !l?._removed).length +
    (form.values.additionalLines ?? []).filter((l: any) => !l?._removed).length;
  const changeCount = countAmendLineChanges(
    form.values.lines,
    form.values.additionalLines,
    originalLines,
    originalAdditionalLines,
  );
  const hasReason = form.values.reason.trim().length > 0;

  useEffect(() => {
    if (formDisabled || !invoiceId) return;
    let cancelled = false;
    const run = async () => {
      setCalculating(true);
      setDryRunError(null);
      try {
        const parsed = JSON.parse(debouncedMoneyKey) as IProposeAmendmentBody;
        const res = await InvoiceApi.proposeAmendment(invoiceId, {
          ...parsed,
          reason: DRY_RUN_REASON_PLACEHOLDER,
          dryRun: true,
        });
        if (!cancelled) {
          setCalculated({
            key: debouncedMoneyKey,
            previousTotal: res.previousTotal,
            newTotal: res.newTotal,
            newVat: (res as { computed?: { totalVatAmount?: number } })?.computed?.totalVatAmount ?? null,
          });
          setDryRunError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setCalculated(null);
          setDryRunError(getAmendErrorMessage(error) ?? t("amend.errors.refused"));
        }
      } finally {
        if (!cancelled) setCalculating(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [debouncedMoneyKey, formDisabled, invoiceId, t]);

  const canSubmit =
    !formDisabled && hasReason && isDirty && !!calculated && calculatedResult?.key === moneyKey && calculated.newTotal >= paid;

  const handleConfirmSubmit = async () => {
    if (!form.values.reason.trim()) {
      message.error(t("amend.reasonRequired"));
      return;
    }
    try {
      setSubmitting(true);
      await InvoiceApi.proposeAmendment(invoiceId, buildProposeBody(form.values, false));
      message.success(t("amend.proposedToast"));
      setConfirmOpen(false);
      navigate(detailsPath);
    } catch (error) {
      handleAmendError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setUnsavedOpen(true);
      return;
    }
    navigate(detailsPath);
  }, [detailsPath, isDirty, navigate]);

  useEffect(() => {
    if (amendability && !amendability.enabled) {
      navigate(detailsPath, { replace: true });
    }
  }, [amendability, detailsPath, navigate]);

  const confirmChanges = useMemo(
    () => buildConfirmChanges(form.values, originalLines, originalAdditionalLines, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- line edits only; skip reason/description keystrokes
    [form.values.lines, form.values.additionalLines, originalLines, originalAdditionalLines, t],
  );

  const diff = calculated ? calculated.newTotal - calculated.previousTotal : 0;
  const newRemaining = calculated != null ? Math.max(0, calculated.newTotal - paid) : 0;
  const vatDiff =
    calculated?.newVat != null && Number.isFinite(calculated.newVat) ? calculated.newVat - previousVat : null;
  const belowPaid = calculated != null && calculated.newTotal < paid;
  const showReopen = invoice?.invoiceStatus === "CLOSED" && calculated != null && !belowPaid && newRemaining > 0;
  const historyItems = amendability?.history ?? [];
  const blocked = Boolean(amendability && !amendability.amendable && amendability.reasons.length > 0);

  if (invoiceLoading || amendLoading) return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>;

  if (!INV_LIST.hasAccess || !INV_AMEND.hasAccess) {
    return <Page>{INV_AMEND.accessDenied()}</Page>;
  }

  if (amendability && !amendability.enabled) {
    return <Box pos="relative" mih={320}><LoadingOverlay visible /></Box>;
  }

  return (
    <Page>
      <Box className={classes.pageHdr}>
        <Box className={classes.pageHdrLeft}>
          <Breadcrumb data={[
              { label: t("invoice:breadcrumb.salesInvoice") },
              { label: t("invoice:detail.crumbOpen"), to: "/sales-invoice" },
              { label: invoice?.refCode ?? "-", to: detailsPath },
              { label: t("amend.breadcrumbShort") },
            ]} />

          <Box className={classes.titleRow}>
            <Title order={1} className={classes.pageTitle}>
              {t("amend.titleShort")}{" "}
              <Box component="span" className={classes.pageTitleMono}>
                {invoice?.refCode ?? ""}
              </Box>
            </Title>
            {(statusRef?.name || invoice?.invoiceStatus) && (
              <Box component="span" className={classes.statusPill}>
                <Box component="span" className={classes.statusDot} />
                {statusRef?.name ?? invoice?.invoiceStatus}
              </Box>
            )}
          </Box>

          <Text className={classes.pageSub}>
            {partnerName || "-"}
            {partnerReg ? ` · ${t("amend.regPrefix")} ${partnerReg}` : ""}
            {confirmedDate ? ` · ${t("invoice:detail.subtitleConfirmed", { date: confirmedDate })}` : ""}
            {dueDate ? ` · ${t("amend.dueShort", { date: dueDate })}` : ""}
          </Text>
        </Box>

        <Box className={classes.pageHdrRight}>
          <Button variant="default" className={classes.actionBtn} onClick={handleCancel}>
            {t("common:cancel")}
          </Button>
        </Box>
      </Box>

      {blocked ? (
        <Box className={classes.blockBanner}>
          <Box className={classes.blockBannerReasons}>
            {amendability!.reasons.map((code) => (
              <Text key={code} size="sm">
                {t(`amend.reasons.${code}`, { defaultValue: code })}
              </Text>
            ))}
          </Box>
          <Button variant="default" className={classes.actionBtn} onClick={() => navigate(detailsPath)}>
            {t("amend.backToDetail")}
          </Button>
        </Box>
      ) : (
        <>
          <Box className={classes.infoBanner}>
            <InfoIcon size={18} className={classes.infoBannerIcon} />
            <Text span>
              {t("amend.infoBannerBefore")}
              <Text span className={classes.infoBannerStrong}>
                {currencyFormat(paid)}
              </Text>
              {t("amend.infoBannerAfter")}
            </Text>
          </Box>

          <Box className={classes.grid}>
            <Box className={classes.colMain}>
              <Box className={classes.card}>
                <Box className={classes.itemsHead}>
                  <Box className={classes.itemsHeadLeft}>
                    <Title order={2} className={classes.cardTitle}>
                      {t("invoice:step.items")}
                    </Title>
                    <Text span className={classes.itemsPill}>
                      {t("amend.lineChangePill", { lines: lineCount, changes: changeCount })}
                    </Text>
                  </Box>
                  <Box className={classes.itemsActions}>
                    <Button
                      className={`${productClasses.sectionAction} ${classes.sectionAction}`}
                      size="sm"
                      variant="default"
                      leftSection={<PlusIcon size={14} />}
                      disabled={formDisabled}
                      onClick={() => productFormRef.current?.openAddManual()}
                    >
                      {t("productForm.addManualLine")}
                    </Button>
                    <Button
                      className={`${productClasses.sectionAction} ${classes.sectionAction}`}
                      size="sm"
                      variant="light"
                      color="brand"
                      leftSection={<PlusIcon size={14} />}
                      disabled={formDisabled || !form.values.receiverBusinessId}
                      onClick={() => productFormRef.current?.openAdd()}
                    >
                      {t("productForm.addItem")}
                    </Button>
                  </Box>
                </Box>

                <Box className={classes.legend}>
                  <Text span className={classes.legendItem}>
                    <Box component="span" className={`${classes.legendSwatch} ${classes.legendChanged}`} />
                    {t("amend.lineChanged")}
                  </Text>
                  <Text span className={classes.legendItem}>
                    <Box component="span" className={`${classes.legendSwatch} ${classes.legendNew}`} />
                    {t("amend.lineNew")}
                  </Text>
                  <Text span className={classes.legendItem}>
                    <Box component="span" className={`${classes.legendSwatch} ${classes.legendRemoved}`} />
                    {t("amend.lineRemoved")}
                  </Text>
                </Box>

                <InvoiceProductForm
                  ref={productFormRef}
                  form={form}
                  mode="amend"
                  hideSummary
                  hideAdjustments
                  originalLines={originalLines}
                  originalAdditionalLines={originalAdditionalLines}
                />
              </Box>

              <Box className={`${classes.card} ${classes.cardPadWide}`}>
                <Title order={2} className={classes.cardTitle}>
                  {t("amend.adjustmentsTitle")}
                </Title>
                <Box className={classes.adjustGrid}>
                  <Box className={classes.descCol}>
                    <Textarea
                      label={t("productForm.description")}
                      placeholder={t("productForm.descriptionPlaceholder")}
                      autosize={false}
                      minRows={4}
                      disabled={formDisabled}
                      {...form.getInputProps("description")}
                    />
                  </Box>
                  <Box className={classes.adjustRight}>
                    <Box>
                      <Text className={classes.fieldLabel} mb={6}>
                        {t("productForm.totalDiscount")}
                      </Text>
                      <Box className={classes.discountRow}>
                        <SegmentedControl
                          className={classes.discountSeg}
                          value={form.values.discountType || "PERCENTAGE"}
                          disabled={formDisabled}
                          data={[
                            { label: "%", value: "PERCENTAGE" },
                            { label: "₮", value: "AMOUNT" },
                          ]}
                          onChange={(v) => {
                            form.setFieldValue("discountType", v);
                            form.setFieldValue("discountValue", undefined);
                          }}
                        />
                        <NumberInput
                          className={classes.amountRight}
                          flex={1}
                          max={(form.values.discountType || "PERCENTAGE") === "PERCENTAGE" ? 100 : 1000000000}
                          placeholder="0"
                          hideControls
                          disabled={formDisabled}
                          rightSection={
                            form.values.discountValue ? (
                              <CloseButton
                                className={classes.clearSection}
                                onClick={() => form.setFieldValue("discountValue", undefined)}
                                variant="transparent"
                                size="sm"
                                c="gray.7"
                              />
                            ) : null
                          }
                          styles={{ section: { pointerEvents: "all" } }}
                          value={form.values.discountValue ?? ""}
                          onChange={(v) => {
                            if (!form.values.discountType) {
                              form.setFieldValue("discountType", "PERCENTAGE");
                            }
                            form.setFieldValue("discountValue", v === "" ? undefined : Number(v));
                          }}
                        />
                      </Box>
                    </Box>
                    <NumberInput
                      className={classes.amountRight}
                      label={t("productForm.shippingAmount")}
                      placeholder="0.00"
                      hideControls
                      disabled={formDisabled}
                      rightSection={
                        form.values.shippingAmount ? (
                          <CloseButton
                            className={classes.clearSection}
                            onClick={() => form.setFieldValue("shippingAmount", undefined)}
                            variant="transparent"
                            size="sm"
                            c="gray.7"
                          />
                        ) : null
                      }
                      styles={{ section: { pointerEvents: "all" } }}
                      {...form.getInputProps("shippingAmount")}
                    />
                  </Box>
                </Box>
              </Box>

              {historyItems.length > 0 && (
                <Box className={`${classes.card} ${classes.cardPadWide}`}>
                  <Title order={2} className={classes.cardTitle}>
                    {t("amend.previousAmendments")}
                  </Title>
                  <Box className={classes.historyList}>
                    {historyItems.map((item) => (
                      <Box key={item.id} className={classes.historyItem}>
                        <Box className={classes.historyDot} />
                        <Box className={classes.historyMain}>
                          <Text className={classes.historyTitle}>
                            <Text component="span" className={classes.historyMono}>
                              {item.refCode}
                            </Text>
                            {" · "}
                            {currencyFormat(item.previousTotal, { withSymbol: false })} →{" "}
                            {currencyFormat(item.newTotal)}
                          </Text>
                          <Text className={classes.historyMeta}>
                            {t(`amend.status.${item.status}`, { defaultValue: item.status })}
                            {" · "}
                            {dateTimeFormat(item.respondedAt ?? item.proposedAt)}
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box className={classes.colSide}>
              <Box className={`${classes.card} ${classes.cardPad}`}>
                <Box className={classes.reasonHead}>
                  <Title order={2} className={classes.cardTitle}>
                    {t("amend.reasonLabel")}{" "}
                    <Text component="span" className={classes.reasonRequired}>
                      *
                    </Text>
                  </Title>
                  <Text className={classes.reasonHint}>
                    {t("amend.reasonCounter", { count: form.values.reason.length, max: 500 })}
                  </Text>
                </Box>
                <Textarea
                  className={classes.reasonArea}
                  placeholder={t("amend.reasonPlaceholder")}
                  maxLength={500}
                  disabled={formDisabled}
                  {...form.getInputProps("reason")}
                />
                <Text className={classes.reasonHint}>{t("amend.reasonHint")}</Text>
              </Box>

              <Box className={`${classes.card} ${classes.cardPad}`}>
                <Box className={classes.totalsHead}>
                  <Title order={2} className={classes.cardTitle}>
                    {t("amend.totalsTitle")}
                  </Title>
                  <Text className={classes.calcStatus}>
                    <Box
                      component="span"
                      className={`${classes.calcDot} ${
                        !isDirty
                          ? classes.calcDotBusy
                          : dryRunError
                            ? classes.calcDotErr
                            : calculating
                              ? classes.calcDotBusy
                              : calculated
                                ? classes.calcDotOk
                                : classes.calcDotBusy
                      }`}
                    />
                    {!isDirty
                      ? t("amend.calcStatusIdle")
                      : dryRunError
                        ? t("amend.calcStatusError")
                        : calculating
                          ? t("amend.calculating")
                          : calculated
                            ? t("amend.calcStatusDone")
                            : t("amend.calculating")}
                  </Text>
                </Box>

                {dryRunError && isDirty ? (
                  <Text size="sm" c="red.8">
                    {dryRunError}
                  </Text>
                ) : !isDirty ? (
                  <Box className={classes.emptyCalc}>
                    <PencilSimpleIcon size={16} />
                    {t("amend.emptyCalcHint")}
                  </Box>
                ) : calculating && !calculated ? (
                  <Text size="sm" c="dimmed">
                    {t("amend.calculating")}
                  </Text>
                ) : calculated ? (
                  <Stack gap={12}>
                    <Box className={classes.totalsHero}>
                      <Text className={classes.totalPrev}>
                        {currencyFormat(calculated.previousTotal, { withSymbol: false })}
                      </Text>
                      <ArrowRightIcon size={14} className={classes.totalArrow} />
                      <Text className={classes.totalNew}>
                        {currencyFormat(calculated.newTotal, { withSymbol: false })}{" "}
                        <Text component="span" className={classes.totalNewUnit}>
                          ₮
                        </Text>
                      </Text>
                    </Box>
                    <Box className={classes.totalsRows}>
                      <Box className={classes.totalsRow}>
                        <Text span>{t("amend.difference")}</Text>
                        <Text span className={classes.totalsDiff}>
                          {formatSignedAmount(diff)}
                        </Text>
                      </Box>
                      {vatDiff != null && (
                        <Box className={classes.totalsRow}>
                          <Text span>{t("amend.vatDifferenceOf")}</Text>
                          <Text span className={classes.totalsRowValue}>
                            {formatSignedAmount(vatDiff)}
                          </Text>
                        </Box>
                      )}
                      <Box className={classes.totalsRow}>
                        <Text span>{t("amend.impactPaid")}</Text>
                        <Text span className={classes.totalsRowValue}>
                          {currencyFormat(paid, { withSymbol: false })}
                        </Text>
                      </Box>
                    </Box>
                    <Box className={classes.remainingBox}>
                      <Text className={classes.remainingLabel}>{t("amend.impactNewRemaining")}</Text>
                      <Text className={classes.remainingValue}>{currencyFormat(newRemaining)}</Text>
                    </Box>
                    {belowPaid && (
                      <Text size="sm" c="red.8">
                        {t("amend.belowPaidAlert", { paid: currencyFormat(paid) })}
                      </Text>
                    )}
                    {showReopen && (
                      <Text size="sm" c="gray.8">
                        {t("amend.reopenAlert", { amount: currencyFormat(newRemaining) })}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Box className={classes.emptyCalc}>
                    <PencilSimpleIcon size={16} />
                    {t("amend.emptyCalcHint")}
                  </Box>
                )}

                <Box className={classes.checklist} mt={4}>
                  <Text className={classes.checkItem}>
                    <Box component="span" className={`${classes.checkMark} ${changeCount > 0 ? classes.checkDone : classes.checkTodo}`}>
                      {changeCount > 0 ? <CheckIcon size={12} weight="bold" aria-hidden /> : null}
                    </Box>
                    {t("amend.checkChanges", { count: changeCount })}
                  </Text>
                  <Text className={classes.checkItem}>
                    <Box component="span" className={`${classes.checkMark} ${hasReason ? classes.checkDone : classes.checkTodo}`}>
                      {hasReason ? <CheckIcon size={12} weight="bold" aria-hidden /> : null}
                    </Box>
                    {t("amend.checkReason")}
                  </Text>
                </Box>

                <Button
                  className={classes.submitBtn}
                  color="brand"
                  fullWidth
                  loading={submitting}
                  disabled={!canSubmit}
                  onClick={() => setConfirmOpen(true)}
                >
                  {t("amend.submit")}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={null}
        size="md"
        withCloseButton={false}
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <AppModalHeader
          confirm
          title={t("amend.confirmTitle")}
          subtitle={
            <>
              <Text component="span" className={classes.monoNum}>
                {invoice?.refCode}
              </Text>
              {" · "}
              {t("amend.confirmSubtitle", { partner: partnerName || "-" })}
            </>
          }
          onClose={() => setConfirmOpen(false)}
        />

        <Stack gap={16} px={24} py={20}>
          {calculated && (
            <>
              <Box className={classes.confirmTotals}>
                <Box>
                  <Text className={classes.confirmTotalsLabel}>{t("amend.confirmCurrentTotal")}</Text>
                  <Text className={classes.confirmTotalsOld}>{currencyFormat(calculated.previousTotal)}</Text>
                </Box>
                <ArrowRightIcon size={18} color="var(--mantine-color-gray-4)" />
                <Box style={{ textAlign: "right" }}>
                  <Text className={classes.confirmTotalsLabel}>{t("amend.confirmNewTotal")}</Text>
                  <Text className={classes.confirmTotalsNew}>{currencyFormat(calculated.newTotal)}</Text>
                </Box>
              </Box>
              <Box className={classes.confirmMeta}>
                <Text span>
                  {t("amend.difference")}{" "}
                  <Text span className={classes.confirmMetaStrong}>
                    {formatSignedAmount(diff)}
                  </Text>
                </Text>
                {vatDiff != null && (
                  <Text span>
                    {t("amend.vatShort")}{" "}
                    <Text span className={classes.confirmMetaValue}>
                      {formatSignedAmount(vatDiff)}
                    </Text>
                  </Text>
                )}
                <Text span>
                  {t("amend.impactNewRemaining")}{" "}
                  <Text span className={classes.confirmMetaValue}>
                    {currencyFormat(newRemaining, { withSymbol: false })}
                  </Text>
                </Text>
              </Box>
            </>
          )}

          <Box>
            <Text className={classes.confirmChangesTitle}>
              {t("amend.confirmChanges", { count: confirmChanges.length })}
            </Text>
            {confirmChanges.map((chg) => {
              const colors = markerPillColor(chg.marker);
              return (
                <Box key={chg.key} className={classes.confirmChange}>
                  <Box
                    component="span"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: 20,
                      padding: "0 8px",
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 500,
                      flexShrink: 0,
                      background: colors.bg,
                      color: colors.color,
                    }}
                  >
                    {chg.marker === "new"
                      ? t("amend.lineNew")
                      : chg.marker === "removed"
                        ? t("amend.lineRemoved")
                        : t("amend.lineChanged")}
                  </Box>
                  <Text span className={classes.confirmChangeName} title={chg.name}>
                    {chg.name}
                  </Text>
                  <Text span className={classes.confirmChangeDetail}>
                    {chg.detail}
                  </Text>
                </Box>
              );
            })}
          </Box>

          <Box className={classes.confirmReason}>
            <Text className={classes.reasonHint}>{t("amend.reasonLabel")}</Text>
            <Text size="sm">{form.values.reason}</Text>
          </Box>
        </Stack>

        <ModalFooter>
          <Button variant="default" onClick={() => setConfirmOpen(false)}>
            {t("amend.confirmBack")}
          </Button>
          <Button color="brand" loading={submitting} onClick={handleConfirmSubmit}>
            {t("amend.confirmSubmit")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        opened={unsavedOpen}
        onClose={() => setUnsavedOpen(false)}
        title={t("amend.unsavedTitle")}
        size="sm"
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <Stack gap="md" px={24} pb={20}>
          <Text size="sm">{t("amend.unsavedMessage")}</Text>
        </Stack>
        <ModalFooter>
          <Button variant="default" onClick={() => setUnsavedOpen(false)}>
            {t("common:cancel")}
          </Button>
          <Button color="red" variant="light" onClick={() => navigate(detailsPath)}>
            {t("amend.unsavedLeave")}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
}
